import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMMKVString } from 'react-native-mmkv';

import apiClient from '@/constants/http';
import { storage, storageKeys } from '@/constants/storage';
import { useHasSession } from '@/hooks/auth/useHasSession';
import { APPOINTMENT_LOOKUP_QUERY_KEY } from '@/hooks/queries/appointment/useAppointmentLookupQuery';
import { isRetryableHttpError } from '@/utils/apiErrors';
import { forceLogout } from '@/utils/sessionUtils';
import { Tracker } from '@/utils/tracking';

import type { Customer, UseCustomerQueryProps } from './types';

const fetchCustomer = async (
  customerId?: string,
  include?: string[],
): Promise<Customer> => {
  if (!customerId) {
    throw new Error('Customer ID is required');
  }

  const params = include ? { include: include.join(',') } : {};
  const { data } = await apiClient.get<Customer>(`/v1/customer/${customerId}`, {
    params,
  });

  storage.set(storageKeys.customerId, data.id);
  return data;
};

/**
 * The api-client can reject with `undefined` or a plain `{ code, message }`
 * body instead of an Error, so the message is read defensively.
 */
export const shouldRetryCustomerQuery = (
  failureCount: number,
  error: unknown,
): boolean => {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  if (typeof message === 'string' && message.toLowerCase().includes('token')) {
    return false;
  }
  return isRetryableHttpError(error) && failureCount < 3;
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const customerQueryOptions = (
  customerId: string | undefined,
  include: string[] = ['photos'],
) => ({
  queryKey: ['customer', customerId ?? '', { include }] as const,
  queryFn: () => fetchCustomer(customerId, include),
  gcTime: ONE_DAY_MS,
  staleTime: ONE_DAY_MS,
  retry: shouldRetryCustomerQuery,
});

export const useCustomerQuery = (options?: UseCustomerQueryProps) => {
  const queryClient = useQueryClient();

  const [_customerId = '', setCustomerId] = useMMKVString(
    storageKeys.customerId,
    storage,
  );

  const {
    enabled = true,
    customerId = _customerId,
    include = ['photos'],
  } = options || {};

  const { queryKey, ...customerOptions } = customerQueryOptions(
    customerId,
    include,
  );
  const hasSession = useHasSession();

  const query = useQuery<Customer | null>({
    ...options,
    ...customerOptions,
    queryKey,
    enabled: !!customerId && hasSession && enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const logoutCustomer = () => {
    Tracker.track('sign_out', {
      customerId: customerId || '',
    });

    forceLogout();
    apiClient.clearTokenPair();

    setCustomerId('');

    queryClient.removeQueries({ queryKey });
    queryClient.removeQueries({
      exact: false,
      queryKey: APPOINTMENT_LOOKUP_QUERY_KEY,
    });
  };

  return { ...query, customerId, setCustomerId, logoutCustomer };
};
